import React, { useEffect, useState } from 'react';
import './styles/main.css';
import { fetchNewsPosts } from './lib/authClient';

const INITIAL_POST_LIMIT = 24;
const POSTS_PAGE_SIZE = 12;
const AD_SLOT_INTERVAL = 6;

const formatDateTime = (value) => {
  if (!value) return '';

  try {
    return new Date(value).toLocaleString('ru-RU', {
      day: '2-digit',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
};

const getPreviewHost = (url) => {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};

const URL_PATTERN = /(https?:\/\/[^\s<]+)/gi;

const renderTextWithLinks = (text, keyPrefix) => {
  const parts = String(text || '').split(URL_PATTERN);

  return parts.map((part, index) => {
    if (!part) return null;

    if (URL_PATTERN.test(part)) {
      URL_PATTERN.lastIndex = 0;
      return (
        <a
          key={`${keyPrefix}-link-${index}`}
          className="news-inline-link"
          href={part}
          target="_blank"
          rel="noopener noreferrer"
        >
          {part}
        </a>
      );
    }

    URL_PATTERN.lastIndex = 0;
    return <React.Fragment key={`${keyPrefix}-text-${index}`}>{part}</React.Fragment>;
  });
};

const ImageLightbox = ({ imageSet, onClose, onPrev, onNext }) => {
  if (!imageSet?.items?.length) return null;

  const currentImage = imageSet.items[imageSet.index] || imageSet.items[0];
  const hasMultiple = imageSet.items.length > 1;

  return (
    <div
      className="image-lightbox"
      onClick={onClose}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Escape' || event.key === 'Enter' || event.key === ' ') {
          onClose();
        }
      }}
    >
      <button
        className="image-lightbox-close"
        onClick={onClose}
        aria-label="Закрыть изображение"
      >
        ×
      </button>
      {hasMultiple && (
        <button
          className="image-lightbox-nav prev"
          onClick={(event) => {
            event.stopPropagation();
            onPrev();
          }}
          aria-label="Предыдущее изображение"
        >
          ‹
        </button>
      )}
      {hasMultiple && (
        <button
          className="image-lightbox-nav next"
          onClick={(event) => {
            event.stopPropagation();
            onNext();
          }}
          aria-label="Следующее изображение"
        >
          ›
        </button>
      )}
      <div className="image-lightbox-frame" onClick={(event) => event.stopPropagation()}>
        <img src={currentImage.src} alt={currentImage.alt} />
        {hasMultiple && (
          <div className="image-lightbox-counter">
            {imageSet.index + 1} / {imageSet.items.length}
          </div>
        )}
      </div>
    </div>
  );
};

const NewsMedia = ({ post, onOpenImage }) => {
  const media = Array.isArray(post.mediaURLs) ? post.mediaURLs : [];
  if (!media.length) return null;

  const items = media.map((src, index) => ({
    src,
    alt: index === 0 ? 'Иллюстрация к новости' : `Иллюстрация ${index + 1}`,
  }));

  if (media.length === 1) {
    return (
      <button
        type="button"
        className="news-media-single"
        onClick={() => onOpenImage({ items, index: 0 })}
      >
        <img src={media[0]} alt="Иллюстрация к новости" loading="lazy" />
      </button>
    );
  }

  return (
    <div className="news-media-grid">
      {media.slice(0, 4).map((mediaURL, index) => (
        <button
          type="button"
          key={`${post.id}-${index}`}
          className="news-media-tile"
          onClick={() => onOpenImage({ items, index })}
        >
          <img src={mediaURL} alt={`Иллюстрация ${index + 1}`} loading="lazy" />
        </button>
      ))}
    </div>
  );
};

const LinkPreview = ({ preview }) => {
  if (!preview?.url) return null;

  return (
    <a
      className="news-preview"
      href={preview.url}
      target="_blank"
      rel="noopener noreferrer"
    >
      {preview.imageURL && (
        <div className="news-preview-image">
          <img src={preview.imageURL} alt={preview.title || 'Превью ссылки'} loading="lazy" />
        </div>
      )}
      <div className="news-preview-copy">
        <span className="news-preview-site">{preview.siteName || getPreviewHost(preview.url)}</span>
        {preview.title && <strong>{preview.title}</strong>}
        {preview.description && <p>{preview.description}</p>}
      </div>
    </a>
  );
};

const buildFeedItems = (posts) => {
  const items = [];
  let adSlotIndex = 1;

  posts.forEach((post, index) => {
    items.push({ type: 'post', post });

    if ((index + 1) % AD_SLOT_INTERVAL === 0) {
      items.push({
        type: 'ad',
        id: `ad-slot-${adSlotIndex}`,
        slotName: `feed-inline-${adSlotIndex}`,
        order: adSlotIndex,
      });
      adSlotIndex += 1;
    }
  });

  return items;
};

const Main = () => {
  const [posts, setPosts] = useState([]);
  const [hasMorePosts, setHasMorePosts] = useState(false);
  const [nextOffset, setNextOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [openedImageSet, setOpenedImageSet] = useState(null);
  const feedItems = buildFeedItems(posts);

  useEffect(() => {
    let cancelled = false;

    const loadInitialPosts = async () => {
      setError('');
      setIsLoading(true);

      try {
        const response = await fetchNewsPosts(INITIAL_POST_LIMIT, 0);
        if (cancelled) return;
        const nextPosts = Array.isArray(response?.posts) ? response.posts : [];
        setPosts(nextPosts);
        setHasMorePosts(Boolean(response?.hasMore));
        setNextOffset(Number.isFinite(response?.nextOffset) ? response.nextOffset : nextPosts.length);
      } catch (loadError) {
        if (cancelled) return;
        setError(loadError.message || 'Не удалось загрузить новости.');
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    };

    loadInitialPosts();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleLoadMore = async () => {
    if (isLoadingMore || !hasMorePosts) return;

    setIsLoadingMore(true);
    setError('');

    try {
      const response = await fetchNewsPosts(POSTS_PAGE_SIZE, nextOffset);
      const incomingPosts = Array.isArray(response?.posts) ? response.posts : [];

      setPosts((current) => {
        const knownIds = new Set(current.map((post) => post.id));
        const appended = incomingPosts.filter((post) => !knownIds.has(post.id));
        return [...current, ...appended];
      });
      setHasMorePosts(Boolean(response?.hasMore));
      setNextOffset(Number.isFinite(response?.nextOffset) ? response.nextOffset : nextOffset + incomingPosts.length);
    } catch (loadError) {
      setError(loadError.message || 'Не удалось загрузить ещё новости.');
    } finally {
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!openedImageSet) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpenedImageSet(null);
      }

      if (event.key === 'ArrowLeft') {
        setOpenedImageSet((current) => {
          if (!current?.items?.length) return current;
          return {
            ...current,
            index: (current.index - 1 + current.items.length) % current.items.length,
          };
        });
      }

      if (event.key === 'ArrowRight') {
        setOpenedImageSet((current) => {
          if (!current?.items?.length) return current;
          return {
            ...current,
            index: (current.index + 1) % current.items.length,
          };
        });
      }
    };

    document.body.classList.add('lock');
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.classList.remove('lock');
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [openedImageSet]);

  return (
    <main className="content">
      <section className="news-hero">
        <span className="news-kicker">Лента МТВ</span>
        <h1>Свежие новости канала теперь прямо на сайте</h1>
        <p>
          Мы регулярно обновляем эту страницу, чтобы вы всегда были в курсе последних публикаций нашего Telegram-канала. Здесь вы найдете анонсы новых выпусков, эксклюзивные материалы и многое другое.
        </p>
        <a
          className="tg-channel"
          href="https://t.me/kanal_mtv2019"
          target="_blank"
          rel="noopener noreferrer"
        >
          Открыть Telegram-канал
        </a>
      </section>

      {isLoading && (
        <section className="news-state">
          <h2>Загружаем новости...</h2>
        </section>
      )}

      {!isLoading && error && (
        <section className="news-state error">
          <h2>Лента временно недоступна</h2>
          <p>{error}</p>
        </section>
      )}

      {!isLoading && !error && posts.length === 0 && (
        <section className="news-state">
          <h2>Новостей пока нет</h2>
          <p>Как только в канале появятся публикации, они отобразятся здесь.</p>
        </section>
      )}

      {!isLoading && !error && posts.length > 0 && (
        <>
          <section className="news-feed">
            {feedItems.map((item) => {
              if (item.type === 'ad') {
                return (
                  <article
                    key={item.id}
                    className="news-ad-slot"
                    data-ad-slot={item.slotName}
                    data-ad-order={item.order}
                  >
                    <div className="news-ad-slot-head">
                      <span className="news-ad-badge">Реклама</span>
                    </div>
                    <div className="news-ad-slot-body">
                      <strong>Место под рекламный блок</strong>
                      <p>
                        Скоро тут будет рекламное объявление. Если вас интересует размещение рекламы в ленте МТВ,{' '}
                        <a href="mailto:ad@mtv-media.ru">напишите нам</a>
                      </p>
                    </div>
                  </article>
                );
              }

              const { post } = item;

              return (
                <article key={post.id} className="news-card">
                  <div className="news-card-top">
                    <span className="news-badge">Новость</span>
                    <time dateTime={post.createdAt}>{formatDateTime(post.createdAt)}</time>
                  </div>

                  <div className="news-text">
                    {String(post.text || '')
                      .split('\n')
                      .filter((line, index, lines) => line.trim() || (index > 0 && index < lines.length - 1))
                      .map((line, index) => (
                        <p key={`${post.id}-line-${index}`}>
                          {line ? renderTextWithLinks(line, `${post.id}-${index}`) : '\u00A0'}
                        </p>
                      ))}
                  </div>

                  <NewsMedia post={post} onOpenImage={setOpenedImageSet} />
                  <LinkPreview preview={post.linkPreviewEnabled ? post.linkPreview : null} />

                  <div className="news-actions">
                    {post.postURL && (
                      <a
                        className="news-link"
                        href={post.postURL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Открыть пост
                      </a>
                    )}
                  </div>
                </article>
              );
            })}
          </section>

          {hasMorePosts && (
            <section className="news-load-more-wrap">
              <div className="news-load-more-panel">
                <button
                  type="button"
                  className="news-load-more"
                  disabled={isLoadingMore}
                  onClick={handleLoadMore}
                >
                  {isLoadingMore ? 'Загружаем ещё...' : 'Показать ещё'}
                </button>
              </div>
            </section>
          )}
        </>
      )}

      <ImageLightbox
        imageSet={openedImageSet}
        onClose={() => setOpenedImageSet(null)}
        onPrev={() => {
          setOpenedImageSet((current) => {
            if (!current?.items?.length) return current;
            return {
              ...current,
              index: (current.index - 1 + current.items.length) % current.items.length,
            };
          });
        }}
        onNext={() => {
          setOpenedImageSet((current) => {
            if (!current?.items?.length) return current;
            return {
              ...current,
              index: (current.index + 1) % current.items.length,
            };
          });
        }}
      />
    </main>
  );
};

export default Main;
